import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef, useState } from "react";
import { AUTH_ROLES } from "./types";
import { parseJwtRoles } from "../lib/roles";

const ADMIN_ROLE = AUTH_ROLES[0];
const PARTNER_ROLE = AUTH_ROLES[1];

export function useAuthRoles() {
  const { isAuthenticated, isLoading: isAuthLoading, getClaim, getRoles, user } = useKindeAuth();
  const getClaimRef = useRef(getClaim);
  const getRolesRef = useRef(getRoles);
  getClaimRef.current = getClaim;
  getRolesRef.current = getRoles;

  const [roles, setRoles] = useState<string[]>([]);
  const [isRolesLoading, setIsRolesLoading] = useState(true);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }
    if (!isAuthenticated) {
      setRoles([]);
      setIsRolesLoading(false);
      return;
    }

    let cancelled = false;
    setIsRolesLoading(true);

    void (async () => {
      try {
        const claim = await getClaimRef.current<Record<string, unknown>, unknown>("roles");
        let next = parseJwtRoles(claim?.value);
        if (next.length === 0) {
          next = parseJwtRoles(await getRolesRef.current());
        }
        if (!cancelled) {
          setRoles(next);
        }
      } catch {
        if (!cancelled) {
          setRoles([]);
        }
      } finally {
        if (!cancelled) {
          setIsRolesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isAuthLoading, user?.id]);

  return {
    roles,
    isAdmin: roles.includes(ADMIN_ROLE),
    isPartner: roles.includes(PARTNER_ROLE),
    isLoading: isAuthLoading || isRolesLoading,
  };
}
